import React, { useCallback, useEffect } from "react";
import { ContractValuesDetails, ScreenResponsive, SimulationSummary as SummaryComponent } from "../../components";
import { observer } from "mobx-react-lite";
import { Box, Grid, skinVars, Text6 } from "@telefonica/mistica";
import { strings } from "../../localization";
import { useGlobalStore } from "../../contexts/globalStoreContext";
import { useAnalytics } from "../../hooks/useAnalytics";
import { RMKTRotSimulation } from "../../services";
import { TabSelectedEnum } from "../../stores";
import { useNavigateCustom } from "../../hooks";
import { DocumentosCopyBiometryAnalysisStatus, ReasonsCode } from "../../enum";

interface Installment {
	index: number;
	data: string;
	value: number;
}

interface Fields {
	IOF: number;
	anticipationValue: number;
	anticipationYears: number;
	blockedFGTSValue: number;
	contractFees: number;
	monthFees: number;
	yearsFees: number;
	tacAmount: number;
}

const SimulatorSummary: React.FC = observer(() => {
	const navigate = useNavigateCustom();
	const { anticipationStore: store, fintechRegisterStore, fraudAnalysisStore } = useGlobalStore();
	const tabSelected = store.getTabSelected();
	const isPartial = tabSelected === TabSelectedEnum.PARTIAL;
	const { addAnalyticEvent, updatePageView, addAnalyticEventPurchase } = useAnalytics();

	const handleAnalyticInteraction = (description: string): void => {
		addAnalyticEvent({
			component: "body",
			action: "click.button",
			description,
			step: "fgts:simulador-quente:resumo",
			value: isPartial ? partialData.value?.disbursedIssueAmount : totalData.disbursedIssueAmount.value,
		}, { applySanitize: true });
	};

	const handleAnalyticPurchase = (): void => {
		addAnalyticEventPurchase({
			event: "add_to_cart",
			ecommerce: {
				value: isPartial ? partialData.value?.disbursedIssueAmount : totalData.disbursedIssueAmount.value,
			},
			item: {
				item_name: "antecipacao-parcial-fgts",
				price: isPartial ? partialData.value?.disbursedIssueAmount : totalData.disbursedIssueAmount.value,
				item_id: "12345",
			},
		});
	};

	const partialData = store.partialAnticipationShelf.partialSimulationData;
	const totalData = store.totalAnticipationShelf;

	const { anticipation } = strings.pages.navigation;
	const summaryText = strings.pages.simulatorSummary.summaryText;

	useEffect(() => {
		updatePageView({
			name: "Vivo Money FGTS Simulador Quente Resumo",
			pagePath: "/fgts/simulador-quente/resultado/resumo",
		});
	}, []);

	const onBack = (): void => {
		addAnalyticEvent({
			component: "header",
			action: "click.button",
			description: anticipation,
			step: "fgts:simulador-quente:resumo",
			value: isPartial ? partialData.value?.disbursedIssueAmount : totalData.disbursedIssueAmount.value,
		}, { applySanitize: true });
		navigate(-1);
	};

	const renderFields = useCallback((): Fields => {
		if (isPartial) {
			return {
				IOF: partialData.value?.iofAmount || 0,
				anticipationValue: partialData.value?.disbursedIssueAmount || 0,
				anticipationYears: partialData.value?.yearsInAdvance || 0,
				blockedFGTSValue: partialData.value?.totalAmount || 0,
				contractFees: partialData.value?.totalPreFixedAmount || 0,
				monthFees: partialData.value?.monthlyRate || 0,
				yearsFees: partialData.value?.annualRate || 0,
				tacAmount: partialData.value?.tacAmount || 0,
			};
		}

		return {
			IOF: totalData.IOF.value,
			anticipationValue: totalData.disbursedIssueAmount.value,
			anticipationYears: totalData.yearsAdvance.value,
			blockedFGTSValue: totalData.blockedValue.value,
			contractFees: totalData.contractFees.value,
			monthFees: totalData.monthFees.value || 0,
			yearsFees: totalData.yearsFees.value || 0,
			tacAmount: totalData.tacAmount.value || 0,
		};
	}, [tabSelected]);

	const renderInstallments = useCallback((): Installment[] => {
		const installments: Installment[] = [];
		if (isPartial) {
			partialData.value?.installments.map((installment, index) =>
				installments.push({ index, data: installment.dueDate, value: installment.totalAmount }),
			);

			return installments;
		}

		totalData.installments.value.map((installment, index) =>
			installments.push({ index, data: installment.dueDate, value: installment.totalAmount }),
		);

		return installments;
	}, [tabSelected]);

	const handleAlterValue = (): void => {
		handleAnalyticInteraction(strings.components.simulationSummary.secondaryButton);
		store.onEditPartialValue();
		navigate("/anticipation");
	};

	const handleAnticipation = async (): Promise<void> => {
		handleAnalyticInteraction(strings.components.simulationSummary.primaryButton);
		handleAnalyticPurchase();
		RMKTRotSimulation({
			disbursedIssueAmout: String(renderFields().anticipationValue),
			yearsInAdvance: String(renderFields().anticipationYears),
			document: fintechRegisterStore.personal.value?.cpfNumber || "",
			totalAmount: String(renderFields().blockedFGTSValue),
		});

		if (!fintechRegisterStore.personal.value?.alreadyRegistration) {
			return navigate("/fintech-registration/user-registration-data");
		}

		// validar fraudAnalysis e redirecionar
		await fraudAnalysisStore.getFraudAnalysis();

		if (fraudAnalysisStore.statusDocumentosCopy === DocumentosCopyBiometryAnalysisStatus.RESEND_NEEDED) {
			if (fraudAnalysisStore.statusReason === ReasonsCode.DOCUMENT_ERROR) {
				return navigate("/error-old-document");
			}

			if (fraudAnalysisStore.statusReason === ReasonsCode.UNDERAGE_DOCUMENT_ERROR) {
				return navigate("/underage-costumer");
			}
		}

		navigate("/fintech-registration/validate-documents/home", { state: { statusDocumentosCopy: fraudAnalysisStore.statusDocumentosCopy } });
	};

	return (
		<ScreenResponsive
			isFooter
			isLogo
			navigationTitle={anticipation}
			onBack={onBack}
			responsiveLayoutProps={{ backgroundColor: skinVars.colors.backgroundAlternative }}
		>
			<Grid justifyContent="center">
				<Grid alignItems="center" columns={{ maxSize: 392 }}>
					<Box paddingTop={24} paddingBottom={56}>
						<Box paddingBottom={24}>
							<Text6 color={skinVars.colors.textPrimary}>{summaryText}</Text6>
						</Box>
						<SummaryComponent
							amountReceipt={renderFields().anticipationValue}
							onAnticipation={() => {
								handleAnticipation();
							}}
							onAlterValue={handleAlterValue}
							extra={
								<ContractValuesDetails
									IOF={renderFields().IOF}
									anticipationValue={renderFields().anticipationValue}
									anticipationYears={renderFields().anticipationYears}
									blockedFGTSValue={renderFields().blockedFGTSValue}
									contractFees={renderFields().contractFees}
									monthFees={renderFields().monthFees}
									yearsFees={renderFields().yearsFees}
									tacAmount={renderFields().tacAmount}
								/>
							}
							collapsibleDataTAnalytic={() => handleAnalyticInteraction(strings.components.debitFGTSInstallments.title)}
							collapsibleData={renderInstallments()}
						/>
					</Box>
				</Grid>
			</Grid>
		</ScreenResponsive>
	);
});

export default SimulatorSummary;
